import { useEffect, useRef } from 'react';

import { useAuth } from '../../auth/useAuth';
import { useProvisionCurrentUserMutation } from '../../gql/generated';
import {
  EdmsProvisioningSnapshot,
  setEdmsProvisioningState,
} from './edmsProvisioningState';

const normalizeProvisioningSnapshot = (
  nextState: Partial<EdmsProvisioningSnapshot>
): Partial<EdmsProvisioningSnapshot> => {
  if (nextState.status !== 'failed' || !nextState.message) {
    return nextState;
  }

  const message = nextState.message.toLowerCase();
  if (message.includes('already exists')) {
    return {
      ...nextState,
      status: 'already_exists',
      message: null,
    };
  }

  return nextState;
};

const updateEdmsProvisioningState = (
  nextState: Partial<EdmsProvisioningSnapshot>
) => {
  setEdmsProvisioningState({
    status: 'unknown',
    updatedAt: null,
    ...normalizeProvisioningSnapshot(nextState),
  });
};

export const EdmsProvisioning = () => {
  const { isAuthenticated, user } = useAuth();
  const [provisionCurrentUser, { data, error }] =
    useProvisionCurrentUserMutation();

  const hasRequestedRef = useRef(false);
  const lastUserEmailRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (user?.email !== lastUserEmailRef.current) {
      lastUserEmailRef.current = user?.email;
      hasRequestedRef.current = false;
    }
  }, [user?.email]);

  useEffect(() => {
    if (!isAuthenticated || !user?.email || hasRequestedRef.current) {
      return;
    }

    hasRequestedRef.current = true;

    updateEdmsProvisioningState({
      status: 'in_progress',
      updatedAt: Date.now(),
    });

    provisionCurrentUser().catch((mutationError) => {
      updateEdmsProvisioningState({
        status: 'failed',
        message:
          mutationError instanceof Error
            ? mutationError.message
            : 'Failed to provision EDMS user.',
        updatedAt: Date.now(),
      });
    });
  }, [isAuthenticated, provisionCurrentUser, user?.email]);

  useEffect(() => {
    if (data?.provisionCurrentUser) {
      updateEdmsProvisioningState({
        status: data.provisionCurrentUser.status,
        message: data.provisionCurrentUser.message,
        retryAfterMs: data.provisionCurrentUser.retryAfterMs,
        updatedAt: Date.now(),
      });
    }

    if (error) {
      updateEdmsProvisioningState({
        status: 'failed',
        message: error.message,
        updatedAt: Date.now(),
      });
    }
  }, [data?.provisionCurrentUser, error]);

  return null;
};
